import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CreditCard, Plus, Trash2, Check, Clock, AlertTriangle, Loader2, Upload, Share2, X, Download,
} from 'lucide-react';

import { apiService } from '../services/api';
import { CompanyLock, CompanyGroup, NfcCardSummary } from '../types';
import Modal from './ui/Modal';

interface Props {
  companyId: string;
}

const UID_RE = /^[0-9A-Fa-f]{14}$/;
const stripHex = (s: string) => s.replace(/[^0-9A-Fa-f]/g, '').toUpperCase();

// Downloadable example so admins have a CSV to overwrite with their own cards.
const NFC_IMPORT_EXAMPLE = [
  'uuid_hex,name',
  '04A1B2C3D4E5F6,Driver #12',
  '0479BACD112233,Warehouse master',
].join('\r\n');

// Triggers a client-side CSV download (BOM-prefixed so Excel reads UTF-8 correctly).
const downloadCsvTemplate = (filename: string, content: string) => {
  const blob = new Blob([`﻿${content}\r\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** Parse a CSV/TSV of cards. Accepts `uuid_hex[,name]` per row, tolerates a header
 * and UIDs with separators (04:A1:B2…). Dedupes and reports invalid rows. */
function parseCsv(text: string): { cards: { uuid_hex: string; name: string }[]; invalid: number } {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const cards: { uuid_hex: string; name: string }[] = [];
  const seen = new Set<string>();
  let invalid = 0;
  lines.forEach((line, idx) => {
    const cols = line.split(/[,;\t]/).map((c) => c.trim());
    const uid = stripHex(cols[0] || '');
    // Skip an obvious header row
    if (idx === 0 && !UID_RE.test(uid) && /u?u?id|hex|card|tarjeta/i.test(cols[0] || '')) return;
    if (!UID_RE.test(uid)) { invalid++; return; }
    if (seen.has(uid)) return;
    seen.add(uid);
    cards.push({ uuid_hex: uid, name: cols[1] || '' });
  });
  return { cards, invalid };
}

/** Bulk NFC authorization console. Authorize a card (or a whole CSV) on many locks at once,
 * share an existing card to more locks, and revoke. The mobile app installs/removes on each
 * lock automatically over BLE — no need to present the card lock-by-lock. */
const NfcCardsManager: React.FC<Props> = ({ companyId }) => {
  const [cards, setCards] = useState<NfcCardSummary[]>([]);
  const [locks, setLocks] = useState<CompanyLock[]>([]);
  const [groups, setGroups] = useState<CompanyGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  // Shared target selection (used by single-add and CSV import)
  const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  // Single add
  const [uuidHex, setUuidHex] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // CSV import
  const fileRef = useRef<HTMLInputElement>(null);
  const [csvCards, setCsvCards] = useState<{ uuid_hex: string; name: string }[]>([]);
  const [csvInvalid, setCsvInvalid] = useState(0);
  const [csvFileName, setCsvFileName] = useState('');
  const [importing, setImporting] = useState(false);

  // Share existing card to more locks
  const [shareCard, setShareCard] = useState<NfcCardSummary | null>(null);
  const [shareUnitIds, setShareUnitIds] = useState<string[]>([]);
  const [shareGroupIds, setShareGroupIds] = useState<string[]>([]);
  const [sharing, setSharing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [c, l, g] = await Promise.all([
      apiService.getCompanyNfcCards(companyId),
      apiService.getCompanyLocks(companyId),
      apiService.getCompanyGroups(companyId),
    ]);
    if (c.success && c.data) setCards(c.data);
    if (l.success && l.data) setLocks(l.data);
    if (g.success && g.data) setGroups(g.data);
    setLoading(false);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const sortedLocks = useMemo(
    () => [...locks].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [locks]
  );

  const normalizedUid = uuidHex.trim().toUpperCase();
  const uidValid = UID_RE.test(normalizedUid);
  const hasTargets = selectedUnitIds.length > 0 || selectedGroupIds.length > 0;
  const toggle = (setter: React.Dispatch<React.SetStateAction<string[]>>, id: string) =>
    setter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleAuthorize = async () => {
    if (!uidValid || !hasTargets || submitting) return;
    setSubmitting(true); setMessage(null);
    const res = await apiService.assignNfcCard(companyId, {
      uuid_hex: normalizedUid,
      name: name.trim() || undefined,
      unit_ids: selectedUnitIds.length ? selectedUnitIds : undefined,
      group_ids: selectedGroupIds.length ? selectedGroupIds : undefined,
    });
    setSubmitting(false);
    if (res.success) {
      const d = res.data || {};
      setMessage({ kind: 'ok', text: `Authorized on ${d.targets ?? 0} lock(s) — ${d.created ?? 0} new, ${d.reactivated ?? 0} reactivated, ${d.skipped ?? 0} already active.` });
      setUuidHex(''); setName('');
      load();
    } else {
      setMessage({ kind: 'error', text: res.message || 'Failed to authorize card' });
    }
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const { cards: parsed, invalid } = parseCsv(String(reader.result || ''));
      setCsvCards(parsed);
      setCsvInvalid(invalid);
    };
    reader.readAsText(file);
    e.target.value = ''; // allow re-selecting the same file
  };

  const handleImport = async () => {
    if (!csvCards.length || !hasTargets || importing) return;
    setImporting(true); setMessage(null);
    const res = await apiService.bulkAssignNfcCards(companyId, {
      cards: csvCards,
      unit_ids: selectedUnitIds.length ? selectedUnitIds : undefined,
      group_ids: selectedGroupIds.length ? selectedGroupIds : undefined,
    });
    setImporting(false);
    if (res.success) {
      const d = res.data || {};
      const inv = Array.isArray(d.invalid) ? d.invalid.length : 0;
      setMessage({ kind: 'ok', text: `Imported ${d.cards ?? 0} card(s) on ${d.targets ?? 0} lock(s) — ${d.created ?? 0} new, ${d.reactivated ?? 0} reactivated, ${d.skipped ?? 0} already active${inv ? `, ${inv} invalid skipped` : ''}.` });
      setCsvCards([]); setCsvInvalid(0); setCsvFileName('');
      load();
    } else {
      setMessage({ kind: 'error', text: res.message || 'Import failed' });
    }
  };

  const openShare = (card: NfcCardSummary) => {
    setShareCard(card); setShareUnitIds([]); setShareGroupIds([]);
  };

  const handleShare = async () => {
    if (!shareCard || (!shareUnitIds.length && !shareGroupIds.length) || sharing) return;
    setSharing(true); setMessage(null);
    const res = await apiService.assignNfcCard(companyId, {
      uuid_hex: shareCard.uuid_hex,
      name: shareCard.name || undefined,
      unit_ids: shareUnitIds.length ? shareUnitIds : undefined,
      group_ids: shareGroupIds.length ? shareGroupIds : undefined,
    });
    setSharing(false);
    if (res.success) {
      const d = res.data || {};
      setMessage({ kind: 'ok', text: `Card added to ${d.created ?? 0} more lock(s).` });
      setShareCard(null);
      load();
    } else {
      setMessage({ kind: 'error', text: res.message || 'Failed to share card' });
    }
  };

  const handleRevokeAll = async (card: NfcCardSummary) => {
    if (!window.confirm(`Revoke card ${card.uuid_hex} from ALL ${card.units.length} lock(s)?`)) return;
    setMessage(null);
    const res = await apiService.revokeNfcCard(companyId, { uuid_hex: card.uuid_hex });
    if (res.success) { setMessage({ kind: 'ok', text: `Card ${card.uuid_hex} revoked. Pending removal from locks.` }); load(); }
    else setMessage({ kind: 'error', text: res.message || 'Failed to revoke card' });
  };

  const handleRevokeUnit = async (card: NfcCardSummary, unitId: string) => {
    setMessage(null);
    const res = await apiService.revokeNfcCard(companyId, { uuid_hex: card.uuid_hex, unit_ids: [unitId] });
    if (res.success) load();
    else setMessage({ kind: 'error', text: res.message || 'Failed to revoke from lock' });
  };

  // ── Reusable selectors ──
  const LockList: React.FC<{ selected: string[]; onToggle: (id: string) => void; assigned?: Set<string> }> = ({ selected, onToggle, assigned }) => (
    <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
      {sortedLocks.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">No locks</div>}
      {sortedLocks.map((lock) => {
        const already = assigned?.has(lock.id);
        return (
          <label key={lock.id} className={`flex items-center gap-2 px-3 py-2 text-sm ${already ? 'opacity-50' : 'cursor-pointer hover:bg-slate-50'}`}>
            <input type="checkbox" disabled={already} checked={!!already || selected.includes(lock.id)} onChange={() => !already && onToggle(lock.id)} />
            <span className="text-slate-800">{lock.name || lock.mac_address}</span>
            {already
              ? <span className="ml-auto text-xs text-emerald-600">already</span>
              : <span className="ml-auto font-mono text-xs text-slate-400">{lock.mac_address}</span>}
          </label>
        );
      })}
    </div>
  );

  const GroupList: React.FC<{ selected: string[]; onToggle: (id: string) => void }> = ({ selected, onToggle }) => (
    <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
      {groups.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">No groups</div>}
      {groups.map((group) => (
        <label key={group.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
          <input type="checkbox" checked={selected.includes(group.id)} onChange={() => onToggle(group.id)} />
          <span className="text-slate-800">{group.name}</span>
          <span className="ml-auto text-xs text-slate-400">{group.lock_count} locks</span>
        </label>
      ))}
    </div>
  );

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const shareAssigned = new Set(shareCard?.units.filter((u) => !u.revoked).map((u) => u.unit_id) || []);

  return (
    <div className="space-y-6">
      {message && (
        <div className={`flex items-start gap-2 rounded-lg border px-4 py-3 text-sm ${message.kind === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800'}`}>
          {message.kind === 'ok' ? <Check className="h-4 w-4 mt-0.5" /> : <AlertTriangle className="h-4 w-4 mt-0.5" />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Authorize / import */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="h-5 w-5 text-indigo-600" />
          <h3 className="text-base font-semibold text-slate-900">Authorize cards on locks</h3>
        </div>

        {/* Shared target selection */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-slate-500">Locks ({selectedUnitIds.length} selected)</label>
              {selectedUnitIds.length > 0 && <button onClick={() => setSelectedUnitIds([])} className="text-xs text-slate-400 hover:text-slate-700">Clear</button>}
            </div>
            <LockList selected={selectedUnitIds} onToggle={(id) => toggle(setSelectedUnitIds, id)} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-slate-500">Groups ({selectedGroupIds.length} selected)</label>
              {selectedGroupIds.length > 0 && <button onClick={() => setSelectedGroupIds([])} className="text-xs text-slate-400 hover:text-slate-700">Clear</button>}
            </div>
            <GroupList selected={selectedGroupIds} onToggle={(id) => toggle(setSelectedGroupIds, id)} />
          </div>
        </div>
        {!hasTargets && <p className="mt-2 text-xs text-amber-600">Select at least one lock or group as the target.</p>}

        {/* Single card */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 sm:items-end">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Card UID (14 hex)</label>
            <input
              value={uuidHex}
              onChange={(e) => setUuidHex(e.target.value)}
              placeholder="04A1B2C3D4E5F6"
              className={`w-full rounded-lg border px-3 py-2 font-mono text-sm uppercase ${uuidHex && !uidValid ? 'border-red-300' : 'border-slate-300'}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Name (optional)</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Driver #12" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
          <button
            onClick={handleAuthorize}
            disabled={!uidValid || !hasTargets || submitting}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm disabled:opacity-40"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Authorize
          </button>
        </div>

        {/* CSV import */}
        <div className="mt-5 pt-5 border-t border-slate-100">
          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept=".csv,.txt,text/csv,text/plain" onChange={handleFile} className="hidden" />
            <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 shadow-sm">
              <Upload className="h-4 w-4" />
              Choose CSV…
            </button>
            <button
              type="button"
              onClick={() => downloadCsvTemplate('nfc-cards-example.csv', NFC_IMPORT_EXAMPLE)}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 shadow-sm"
            >
              <Download className="h-4 w-4" />
              Example CSV
            </button>
            <span className="text-xs text-slate-500">
              One row per card: <span className="font-mono">uuid_hex,name</span> (header optional, separators-in-UID OK).
            </span>
          </div>
          {(csvCards.length > 0 || csvInvalid > 0) && (
            <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm">
              <span className="text-slate-700">
                <span className="font-mono text-xs">{csvFileName}</span> — <b>{csvCards.length}</b> valid card(s){csvInvalid > 0 && <>, <span className="text-amber-600">{csvInvalid} invalid</span></>}
              </span>
              <button
                onClick={handleImport}
                disabled={!csvCards.length || !hasTargets || importing}
                className="ml-auto inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40"
              >
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Import {csvCards.length} card(s)
              </button>
              <button onClick={() => { setCsvCards([]); setCsvInvalid(0); setCsvFileName(''); }} className="text-slate-400 hover:text-slate-700" title="Discard"><X className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      </div>

      {/* Existing cards */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-900">Authorized cards ({cards.length})</h3>
        </div>
        {cards.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-400">No NFC cards authorized yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {cards.map((card) => (
              <div key={card.uuid_hex} className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{card.name || 'Unnamed card'}</span>
                      <span className="font-mono text-xs text-slate-500">{card.uuid_hex}</span>
                    </div>
                    <div className="mt-1 text-xs text-slate-400">{card.units.length} lock(s)</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => openShare(card)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50">
                      <Share2 className="h-3.5 w-3.5" />
                      Add to locks
                    </button>
                    <button onClick={() => handleRevokeAll(card)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50">
                      <Trash2 className="h-3.5 w-3.5" />
                      Revoke all
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {card.units.map((u) => (
                    <span
                      key={u.id}
                      title={u.mac_address}
                      className={`group inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs ${
                        u.revoked ? 'border-amber-200 bg-amber-50 text-amber-700'
                          : u.synced ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border-slate-200 bg-slate-50 text-slate-600'}`}
                    >
                      {u.synced && !u.revoked ? <Check className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                      {u.unit_name || u.mac_address}
                      <span className="opacity-70">{u.revoked ? 'removing…' : u.synced ? 'installed' : 'pending'}</span>
                      {!u.revoked && (
                        <button onClick={() => handleRevokeUnit(card, u.unit_id)} className="ml-1 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700" title="Revoke from this lock">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-slate-400">
        Cards must be pre-personalized with the fleet key. "Pending" means queued; the mobile app installs it
        on the lock automatically the next time it connects over Bluetooth.
      </p>

      {/* Share existing card → more locks */}
      <Modal
        open={!!shareCard}
        onClose={() => setShareCard(null)}
        title="Add card to more locks"
        icon={<Share2 className="h-4 w-4 text-indigo-500" />}
        maxWidth="xl"
        footer={
          <>
            <button onClick={() => setShareCard(null)} className="px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
            <button
              onClick={handleShare}
              disabled={(!shareUnitIds.length && !shareGroupIds.length) || sharing}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40"
            >
              {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Add to selected
            </button>
          </>
        }
      >
        {shareCard && (
          <div className="space-y-4">
            <div className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{shareCard.name || 'Unnamed card'}</span>{' '}
              <span className="font-mono text-xs text-slate-500">{shareCard.uuid_hex}</span>
              <div className="mt-1 text-xs text-slate-400">Already on {shareAssigned.size} lock(s). Pick more below.</div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Locks</label>
                <LockList selected={shareUnitIds} onToggle={(id) => toggle(setShareUnitIds, id)} assigned={shareAssigned} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Groups</label>
                <GroupList selected={shareGroupIds} onToggle={(id) => toggle(setShareGroupIds, id)} />
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default NfcCardsManager;
